import { QueryClientProvider } from '@tanstack/react-query';
import type { JSX } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { ProtectedRoute } from '@/components/ProtectedRoute';
import { HomeRedirect, RequirePermission } from '@/components/RequirePermission';
import { AdminsPage } from '@/features/admins/pages/AdminsPage';
import { AuditPage } from '@/features/audit/pages/AuditPage';
import { AcceptInvitationPage } from '@/features/auth/pages/AcceptInvitationPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { MfaPage } from '@/features/auth/pages/MfaPage';
import { MfaSetupPage } from '@/features/auth/pages/MfaSetupPage';
import { DashboardPage } from '@/features/metrics/pages/DashboardPage';
import { PricesPage } from '@/features/pricing/pages/PricesPage';
import { ReportsPage } from '@/features/reports/pages/ReportsPage';
import { EditTenantPage } from '@/features/tenants/pages/EditTenantPage';
import { TenantsPage } from '@/features/tenants/pages/TenantsPage';
import { UsabilityPanelPage } from '@/features/usability/pages/UsabilityPanelPage';
import { AdminLayout } from '@/layouts/AdminLayout';
import { queryClient } from '@/lib/queryClient';

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/mfa" element={<MfaPage />} />
        <Route path="/mfa/setup" element={<MfaSetupPage />} />
        <Route path="/invitation" element={<AcceptInvitationPage />} />

        {/* Protected */}
        <Route
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          {/* OS-1717 — cada sección exige su capacidad; el API la vuelve a exigir. */}
          <Route
            path="/dashboard"
            element={
              <RequirePermission permission="platform.reports.read">
                <DashboardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/tenants"
            element={
              <RequirePermission permission="platform.tenants.read">
                <TenantsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/tenants/:tenantId"
            element={
              <RequirePermission permission="platform.tenants.read">
                <EditTenantPage />
              </RequirePermission>
            }
          />
          <Route
            path="/prices"
            element={
              <RequirePermission permission="platform.billing.read">
                <PricesPage />
              </RequirePermission>
            }
          />
          {/* OS-1724 — /metrics mostraba el mismo dashboard: queda como alias para no romper enlaces. */}
          <Route path="/metrics" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/usability"
            element={
              <RequirePermission permission="platform.reports.read">
                <UsabilityPanelPage />
              </RequirePermission>
            }
          />
          <Route
            path="/reports"
            element={
              <RequirePermission permission="platform.reports.read">
                <ReportsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/audit"
            element={
              <RequirePermission permission="platform.audit.read">
                <AuditPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admins"
            element={
              <RequirePermission permission="platform.admins.manage">
                <AdminsPage />
              </RequirePermission>
            }
          />
          <Route path="/" element={<HomeRedirect />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </QueryClientProvider>
  );
}
